import * as Sentry from "@sentry/tanstackstart-react";
import { StartClient } from "@tanstack/react-start/client";
/// <reference types="vite/client" />
import { hydrateRoot } from "react-dom/client";

import { isAuthError } from "~/lib/auth/auth-error";
import { SENTRY_DSN, SENTRY_PROPAGATION_TARGETS } from "~/lib/sentry-constants";
import { resolveSentryEnvironment } from "~/lib/sentry-environment";
import { SENTRY_IGNORE_ERRORS } from "~/lib/sentry-ignore-errors";
import { SENTRY_SEND_DEFAULT_PII } from "~/lib/sentry-privacy";

import { getRouter } from "./router";

const router = getRouter();

Sentry.init({
  enabled: import.meta.env.PROD,
  environment: resolveSentryEnvironment(
    import.meta.env.VITE_SENTRY_ENVIRONMENT,
    import.meta.env.MODE,
  ),
  dsn: SENTRY_DSN,

  // No default personal data (IPs, request headers, cookies); see
  // `~/lib/sentry-privacy`.
  sendDefaultPii: SENTRY_SEND_DEFAULT_PII,

  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    // NOTE: we deliberately do NOT use captureConsoleIntegration on the client.
    // Forwarding every browser `console.error` turned extension noise, aborted
    // fetches, and third-party library logs into low-signal, stackless issues
    // (see DCM-M4). Instead we rely on: (1) Sentry's automatic global handlers
    // for uncaught errors + unhandled rejections, (2) React error boundaries
    // (root `errorComponent`, DefaultCatchBoundary), and (3) explicit
    // `Sentry.captureException` at catch-sites where a swallowed error is
    // genuinely actionable (payments, data integrity, uploads, core infra).
    // Screen recording of an error is added after analytics consent; see
    // `~/lib/monitoring/error-replay`.
    // Bundled but not auto-injected — surfaced via a topbar button inside the
    // signed-in layout only (see SentryFeedbackButton).
    Sentry.feedbackIntegration({
      autoInject: false,
      colorScheme: "system",
      showBranding: false,
    }),
  ],
  enableLogs: true,

  allowUrls: SENTRY_PROPAGATION_TARGETS,
  tracePropagationTargets: SENTRY_PROPAGATION_TARGETS,

  // Benign, unactionable errors we never want as issues — the list and the
  // rationale for each pattern live in `~/lib/sentry-ignore-errors`.
  ignoreErrors: SENTRY_IGNORE_ERRORS,

  // Drop noisy fetch failures from third-party analytics scripts that are
  // commonly blocked by ad blockers / privacy tooling.
  denyUrls: [/event-store\.sitebehaviour\.com/i],
  beforeSend(event, hint) {
    const error = hint?.originalException;
    // Expected logged-out / expired-session / no-coach conditions. The router
    // already retries (token refresh) and redirects to /login — pure noise.
    if (isAuthError(error)) return null;
    const messages = [
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "",
      event.message ?? "",
      ...(event.exception?.values?.map((v) => v.value ?? "") ?? []),
      ...(event.breadcrumbs
        ?.slice(-5)
        .map((b) => `${b.message ?? ""} ${b.data?.url ?? ""}`) ?? []),
    ];
    if (messages.some((m) => /sitebehaviour\.com/i.test(m))) {
      return null;
    }
    return event;
  },

  tracesSampleRate: 0.1,
  // Read by the replay integration when `startErrorReplay` adds it.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
});

hydrateRoot(document, <StartClient />);
